import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  AiChat,
  AiChatMessage,
  AiChatMessageChunk,
  ChatShort,
  SupportContext,
} from '../common/ai-chat/aiChatTypes';
import type { AiChatFolderMetadata, AgentType, BuildDefinitionStatus } from '@server/ai';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import type { ChatInstance } from './ChatInstance';
import {
  handleArtifactMetadata,
  handleArtifactContent,
  handleBuildDefinitionMetadata,
  handleBuildDefinitionContent,
  handleBuildDefinitionStatus,
  handleFirstTransientChunk,
  handleSubsequentTransientChunk,
  handleMessageContent,
  handleQuestionsMetadataChunk,
  handleQuestionsChunk,
} from './agentsChatsChunkHandlers';
import type { AgentState, AgentsChatsState } from './agentsChatsTypes';
import {
  AGENTS_CHATS_SLICE_NAME,
  GHOST_CHAT_ID,
  TEMP_CHAT_ID,
  getAgentInitialState,
  getChatInitialState,
} from './agentsChatsUtils';

function findChatListItem(agent: AgentState, chatId: string) {
  return agent.chatsList.data?.find((chat) => chat.id === chatId);
}

function upsertSyncMessage(
  agent: AgentState,
  chatId: string,
  message: AiChatMessage,
  agentId: string,
  agentType: AgentType,
  isLoadingResponse: boolean,
) {
  if (!agent.chatsDetails[chatId]) {
    agent.chatsDetails[chatId] = {
      isLoadingChat: false,
      data: getChatInitialState({ agentId, agentType, id: chatId }),
    };
  }
  const chatDetails = agent.chatsDetails[chatId];
  if (!chatDetails.data) {
    chatDetails.data = getChatInitialState({ agentId, agentType, id: chatId });
  }
  if (message.content) {
    chatDetails.data.aiChatDetails.messages.push(message as AiChatMessage);
  }
  chatDetails.data.isLoadingResponse = isLoadingResponse;
}

const initialState: AgentsChatsState = {
  agentIds: {},
  activeAgentId: undefined, // if no active agent, the agents cards are rendered
};

export const agentsChatsSlice = createSlice({
  name: AGENTS_CHATS_SLICE_NAME,
  initialState,
  reducers: {
    setActiveAgent: (
      state,
      action: PayloadAction<{
        agentId: string;
        agentType: AgentType;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, agentType, chatInstanceId } = action.payload;
      state.activeAgentId = agentId;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      const agent = state.agentIds[agentId];
      agent.activeChatIds[chatInstanceId] = GHOST_CHAT_ID;
      agent.chatsDetails[GHOST_CHAT_ID] = {
        isLoadingChat: false,
        data: getChatInitialState({ agentId, agentType, id: GHOST_CHAT_ID }),
      };
      agent.pendingChatFolderId = null;
    },

    resetActiveAgent: (state) => {
      state.activeAgentId = undefined;
    },
    createGhostChat: (
      state,
      action: PayloadAction<{
        agentId: string;
        agentType: AgentType;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, agentType, chatInstanceId } = action.payload;
      const agent = state.agentIds[agentId];
      agent.activeChatIds[chatInstanceId] = GHOST_CHAT_ID;
      agent.chatsDetails[GHOST_CHAT_ID] = {
        isLoadingChat: false,
        data: getChatInitialState({ agentId, agentType, id: GHOST_CHAT_ID }),
      };
    },
    setTempChatId: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatInstanceId: ChatInstance;
      }>,
    ) => {
      const { agentId, chatInstanceId } = action.payload;
      state.agentIds[agentId].activeChatIds[chatInstanceId] = TEMP_CHAT_ID;
    },
    setActiveChat: (
      state,
      action: PayloadAction<{ agentId: string; chatId?: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { chatId, agentId, chatInstanceId } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      state.agentIds[agentId].activeChatIds[chatInstanceId] = chatId;
    },
    setLoadingChatDetails: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { chatId, agentId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      if (!agent.chatsDetails[chatId]) {
        agent.chatsDetails[chatId] = { isLoadingChat: true };
      }
      agent.chatsDetails[chatId].isLoadingChat = true;
    },
    clearLoadingChatDetails: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string }>,
    ) => {
      const { chatId, agentId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const entry = agent.chatsDetails[chatId];
      if (!entry) return;
      entry.isLoadingChat = false;
    },
    setChat: (state, action: PayloadAction<{ agentId: string; chat: AiChat }>) => {
      const { chat, agentId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      agent.chatsDetails[chat.id] = { isLoadingChat: false, data: chat };
    },
    replaceChatMessages: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; messages: AiChatMessage[] }>,
    ) => {
      const { agentId, chatId, messages } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (!chatDetails?.data) return;
      chatDetails.data.aiChatDetails.messages = messages;
    },
    replaceTempWithChatIdAndName: (
      state,
      action: PayloadAction<{
        chatId: string;
        name: string;
        agentId: string;
        agentType: AgentType;
        supportContext?: SupportContext;
        persistedMessages?: AiChatMessage[];
      }>,
    ) => {
      const { chatId, name, agentId, agentType, supportContext, persistedMessages } =
        action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const relevantChat = agent.chatsDetails[TEMP_CHAT_ID]?.data;
      if (!relevantChat) {
        return;
      }
      if (!agent.chatsList) {
        agent.chatsList = { isLoadingChatList: false, data: [] };
      }
      if (!agent.chatsList.data) {
        agent.chatsList.data = [];
      }
      agent.chatsList.data.push({
        id: chatId,
        name,
        updatedAt: new Date().toISOString(),
        isFavorite: false,
        isLoading: false,
        isAnimateName: true,
        agentId,
        agentType,
      });
      agent.chatsDetails[chatId] = {
        isLoadingChat: false,
        data: {
          ...relevantChat,
          id: chatId,
          name,
          ...(supportContext && { supportContext }),
          ...(persistedMessages && {
            aiChatDetails: { ...relevantChat.aiChatDetails, messages: persistedMessages },
          }),
        },
      };
    },
    deleteTempChat: (
      state,
      action: PayloadAction<{
        agentId: string;
      }>,
    ) => {
      const { agentId } = action.payload;
      delete state.agentIds[agentId].chatsDetails[TEMP_CHAT_ID];
    },
    resetAnimateName: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { chatId, agentId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const chatItem = findChatListItem(agent, chatId);
      if (!chatItem) return;
      chatItem.isAnimateName = false;
    },
    addSyncMessageToActiveChat: (
      state,
      action: PayloadAction<{
        message: AiChatMessage;
        agentId: string;
        agentType: AgentType;
        isNewChat?: boolean;
        chatInstanceId: ChatInstance;
        isLoadingResponse?: boolean;
      }>,
    ) => {
      const { message, agentId, agentType, isNewChat, chatInstanceId, isLoadingResponse } =
        action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState(chatInstanceId);
      }
      const agent = state.agentIds[agentId];

      const chatId = isNewChat ? TEMP_CHAT_ID : agent.activeChatIds[chatInstanceId];
      if (!chatId) return;

      if (isNewChat) {
        delete agent.chatsDetails[chatId];
      }
      upsertSyncMessage(agent, chatId, message, agentId, agentType, isLoadingResponse ?? true);
    },
    addSyncMessageToChat: (
      state,
      action: PayloadAction<{
        message: AiChatMessage;
        agentId: string;
        agentType: AgentType;
        chatId: string;
      }>,
    ) => {
      const { message, agentId, agentType, chatId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;

      upsertSyncMessage(agent, chatId, message, agentId, agentType, true);
    },
    patchLastUserMessage: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        persistedMessage: AiChatMessage;
      }>,
    ) => {
      const { agentId, chatId, persistedMessage } = action.payload;
      const messages = state.agentIds[agentId]?.chatsDetails[chatId]?.data?.aiChatDetails?.messages;
      if (!messages) return;
      const lastUserIdx = messages.findLastIndex(
        (m) => m.role === ChatCompletionRequestMessageRole.USER,
      );
      if (lastUserIdx !== -1) {
        messages[lastUserIdx] = persistedMessage;
      }
    },
    addAsyncResponse: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId?: string;
        message: AiChatMessage;
        chatInstanceId?: ChatInstance;
      }>,
    ) => {
      const { message, agentId, chatId, chatInstanceId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;

      const _chatId = chatId ?? (chatInstanceId && agent.activeChatIds[chatInstanceId]);
      if (!_chatId) {
        return;
        //this shouldn't happen
      }

      const chatDetails = agent.chatsDetails[_chatId];
      if (!chatDetails?.data?.aiChatDetails.messages) {
        return;
        //this shouldn't happen
      }
      chatDetails.isLoadingChat = false;
      chatDetails.data.isLoadingResponse = false;
      chatDetails.data.aiChatDetails.messages.push(message);
    },
    addAsyncResponseChunk: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        chunk: AiChatMessageChunk;
        isFirstContentChunk?: boolean;
      }>,
    ) => {
      const { chunk, agentId, chatId, isFirstContentChunk } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;

      const chatDetails = agent.chatsDetails[chatId];
      if (!chatDetails?.data?.aiChatDetails.messages) {
        return;
      }
      chatDetails.isLoadingChat = false;

      // Always set streaming state when any chunk arrives (hides buttons, shows stop button)
      chatDetails.data.isStreamingResponse = true;

      // Only remove loading state for first content chunk
      if (isFirstContentChunk) {
        chatDetails.data.isLoadingResponse = false;
      }

      const messages = chatDetails.data.aiChatDetails.messages;

      // Handle different chunk types with dedicated helper functions
      if (chunk.runId) {
        // Store runId temporarily - no message creation, no UI impact
        chatDetails.pendingRunId = chunk.runId;
        return;
      }

      if (chunk.artifactMetadata) {
        handleArtifactMetadata(chunk, chatDetails, messages);
        return;
      }

      if (chunk.artifactContent && chatDetails.artifactStreamingState) {
        handleArtifactContent(chunk, chatDetails);
        return;
      }

      if (chunk.buildDefinitionMetadata) {
        handleBuildDefinitionMetadata(chunk, chatDetails, messages);
        return;
      }

      if (chunk.buildDefinitionContent && chatDetails.buildDefinitionStreamingState) {
        handleBuildDefinitionContent(chunk, chatDetails);
        return;
      }

      if (chunk.buildDefinitionStatus) {
        handleBuildDefinitionStatus(chunk, chatDetails);
        return;
      }

      // Handle table metadata - store mapping from placeholder to actual tableId
      if (chunk.tableMetadata) {
        if (!chatDetails.tableMetadataMap) {
          chatDetails.tableMetadataMap = {};
        }
        chatDetails.tableMetadataMap[chunk.tableMetadata.placeholderId] =
          chunk.tableMetadata.tableId;
        return;
      }

      if (chunk.questionsMetadata) {
        handleQuestionsMetadataChunk(chatDetails, messages);
        return;
      }

      if (chunk.questionsData) {
        handleQuestionsChunk(chunk, chatDetails, messages);
        return;
      }

      if (chunk.transientChunk && !chunk.content && isFirstContentChunk) {
        handleFirstTransientChunk(chunk.transientChunk, messages, chatDetails);
        return;
      }

      if (chunk.content) {
        handleMessageContent(chunk, chatDetails, messages, isFirstContentChunk ?? false);
      }

      if (chunk.transientChunk && !isFirstContentChunk) {
        handleSubsequentTransientChunk(chunk.transientChunk, messages);
      }
    },
    setResearchInProgress: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; isResearchInProgress: boolean }>,
    ) => {
      const { agentId, chatId, isResearchInProgress } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (!chatDetails) return;
      chatDetails.isResearchInProgress = isResearchInProgress;
    },
    finishAsyncResponse: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { agentId, chatId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const chatDetails = agent.chatsDetails[chatId];
      if (!chatDetails.data) {
        return;
      }

      // Don't finalize streaming while async research is in progress —
      // the SSE stream ends after research_handoff but research continues via WebSocket
      if (chatDetails.isResearchInProgress) {
        return;
      }

      chatDetails.data.isStreamingResponse = false;

      // Clean up artifact streaming state
      if (chatDetails.artifactStreamingState) {
        chatDetails.artifactStreamingState = undefined;
      }

      // Clean up build definition streaming state
      if (chatDetails.buildDefinitionStreamingState) {
        chatDetails.buildDefinitionStreamingState = undefined;
      }

      // Mark animation as not animating for the last message when stream finishes
      const messages = chatDetails.data.aiChatDetails.messages;
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.animationProgress) {
          lastMessage.animationProgress = undefined;
        }
      }
    },
    markArtifactContentProcessed: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; processedLength: number }>,
    ) => {
      const { agentId, chatId, processedLength } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (chatDetails?.artifactStreamingState) {
        chatDetails.artifactStreamingState.lastProcessedLength = processedLength;
      }
    },
    setLoadingChatsList: (
      state,
      action: PayloadAction<{
        agentId: string;
      }>,
    ) => {
      const { agentId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      agent.chatsList.isLoadingChatList = true;
      agent.chatsList.data = undefined;
    },
    setChatsList: (
      state,
      action: PayloadAction<{
        agentId: string;
        chats: ChatShort[];
      }>,
    ) => {
      const { agentId, chats } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      agent.chatsList.isLoadingChatList = false;
      agent.chatsList.data = chats;
    },
    setChatName: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        name: string;
      }>,
    ) => {
      const { agentId, chatId, name } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const chatItem = findChatListItem(agent, chatId);
      if (!chatItem) return;
      chatItem.name = name;
      chatItem.isLoading = false;
      chatItem.isAnimateName = false; //animation is only for the auto-rename

      const chatDetails = agent.chatsDetails[chatId];
      if (chatDetails?.data) {
        chatDetails.data.name = name;
      }
    },
    setLoadingChatListItem: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { agentId, chatId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const chatItem = findChatListItem(agent, chatId);
      if (!chatItem) return;
      chatItem.isLoading = true;
    },
    deleteChat: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { agentId, chatId, chatInstanceId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent?.chatsList?.data) return;
      if (agent.activeChatIds[chatInstanceId] === chatId) {
        agent.activeChatIds[chatInstanceId] = GHOST_CHAT_ID;
      }
      agent.chatsList.data = agent.chatsList.data.filter((chat) => chat.id !== chatId);
    },
    deleteAgentChats: (state, action: PayloadAction<{ agentId: string }>) => {
      const { agentId } = action.payload;
      state.activeAgentId = undefined;
      delete state.agentIds[agentId];
    },
    setChatIsFavorite: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; isFavorite: boolean }>,
    ) => {
      const { agentId, chatId, isFavorite } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      const chatItem = findChatListItem(agent, chatId);
      if (chatItem) {
        chatItem.isFavorite = isFavorite;
      }
    },
    setLoadingSearch: (state, action: PayloadAction<{ agentId: string; searchText: string }>) => {
      const { agentId, searchText } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState();
      }
      state.agentIds[agentId].search = {
        isLoading: true,
        searchText,
      };
    },
    setSearchResult: (state, action: PayloadAction<{ agentId: string; chats: AiChat[] }>) => {
      const { agentId, chats } = action.payload;
      if (!state.agentIds[agentId]) {
        state.agentIds[agentId] = getAgentInitialState();
      }
      const agent = state.agentIds[agentId];
      if (!agent.search) {
        agent.search = {};
      }
      agent.search.isLoading = false;
      agent.search.results = chats;
    },
    clearSearch: (state, action: PayloadAction<{ agentId: string }>) => {
      const { agentId } = action.payload;
      const agent = state.agentIds[agentId];
      if (agent?.search) {
        agent.search = {
          isLoading: false,
        };
      }
    },
    resetActiveChatAndSearch: (
      state,
      action: PayloadAction<{ agentId: string; chatInstanceId: ChatInstance }>,
    ) => {
      const { agentId, chatInstanceId } = action.payload;
      const agent = state.agentIds[agentId];
      if (agent) {
        agent.activeChatIds = {
          ...agent.activeChatIds,
          [chatInstanceId]: undefined,
        };
        agent.search = undefined;
      }
    },
    setMessageAnimationProgress: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        progress: {
          messageCharIndex?: number;
          transientChunkIndex?: number;
        };
      }>,
    ) => {
      const { agentId, chatId, progress } = action.payload;
      const messages = state.agentIds[agentId]?.chatsDetails[chatId]?.data?.aiChatDetails.messages;
      if (!messages) return;

      const lastMessage = messages[messages.length - 1];

      if (!lastMessage.animationProgress) {
        lastMessage.animationProgress = {};
      }

      if (progress.messageCharIndex !== undefined) {
        lastMessage.animationProgress.messageCharIndex = progress.messageCharIndex;
      }
      if (progress.transientChunkIndex !== undefined) {
        lastMessage.animationProgress.transientChunkIndex = progress.transientChunkIndex;
      }
    },
    stopResponse: (state, action: PayloadAction<{ agentId: string; chatId: string }>) => {
      const { agentId, chatId } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (!chatDetails?.data) return;
      chatDetails.data.isLoadingResponse = false;
      chatDetails.data.isStreamingResponse = false;

      // Mark animation as not animating for the last message when stream finishes
      const messages = chatDetails.data.aiChatDetails.messages;
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.animationProgress) {
          lastMessage.animationProgress = undefined;
        }
      }
    },
    setBuildDefinitionStatus: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        buildDefinitionId: string;
        status: BuildDefinitionStatus;
      }>,
    ) => {
      const { agentId, chatId, buildDefinitionId, status } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (!chatDetails?.buildDefinitions?.[buildDefinitionId]) return;
      chatDetails.buildDefinitions[buildDefinitionId].status = status;
    },
    setBuildDefinitionData: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        buildDefinitionId: string;
        text: string;
        status: BuildDefinitionStatus;
      }>,
    ) => {
      const { agentId, chatId, buildDefinitionId, text, status } = action.payload;
      const chatDetails = state.agentIds[agentId]?.chatsDetails[chatId];
      if (!chatDetails) return;
      if (!chatDetails.buildDefinitions) {
        chatDetails.buildDefinitions = {};
      }
      chatDetails.buildDefinitions[buildDefinitionId] = { text, status };
    },
    setFoldersLoading: (state, action: PayloadAction<{ agentId: string }>) => {
      const agent = state.agentIds[action.payload.agentId];
      if (!agent) return;
      agent.folders.isLoading = true;
      agent.folders.data = undefined;
    },
    setFolders: (
      state,
      action: PayloadAction<{ agentId: string; folders: AiChatFolderMetadata[] }>,
    ) => {
      const { agentId, folders } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      agent.folders.isLoading = false;
      agent.folders.data = folders;
    },
    addFolder: (
      state,
      action: PayloadAction<{ agentId: string; folder: AiChatFolderMetadata }>,
    ) => {
      const { agentId, folder } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent) return;
      if (!agent.folders.data) {
        agent.folders.data = [];
      }
      agent.folders.data.push(folder);
    },
    renameFolder: (
      state,
      action: PayloadAction<{ agentId: string; folderId: string; name: string }>,
    ) => {
      const { agentId, folderId, name } = action.payload;
      const folder = state.agentIds[agentId]?.folders.data?.find((f) => f.id === folderId);
      if (folder) {
        folder.name = name;
      }
    },
    deleteFolder: (state, action: PayloadAction<{ agentId: string; folderId: string }>) => {
      const { agentId, folderId } = action.payload;
      const agent = state.agentIds[agentId];
      if (!agent?.folders.data) return;
      agent.folders.data = agent.folders.data.filter((f) => f.id !== folderId);
      // Null out folderId on any chats that were in this folder
      agent.chatsList.data?.forEach((chat) => {
        if (chat.folderId === folderId) {
          chat.folderId = null;
        }
      });
    },
    setChatFolderId: (
      state,
      action: PayloadAction<{ agentId: string; chatId: string; folderId: string | null }>,
    ) => {
      const { agentId, chatId, folderId } = action.payload;
      const chat = state.agentIds[agentId]?.chatsList.data?.find((c) => c.id === chatId);
      if (chat) {
        chat.folderId = folderId;
      }
    },
    setPendingChatFolderId: (
      state,
      action: PayloadAction<{ agentId: string; folderId: string | null }>,
    ) => {
      const { agentId, folderId } = action.payload;
      const agent = state.agentIds[agentId];
      if (agent) {
        agent.pendingChatFolderId = folderId;
      }
    },
    setMessageFeedback: (
      state,
      action: PayloadAction<{
        agentId: string;
        chatId: string;
        runId: string;
        feedback?: 'like' | 'dislike';
      }>,
    ) => {
      const { agentId, chatId, runId, feedback } = action.payload;
      const messages = state.agentIds[agentId]?.chatsDetails[chatId]?.data?.aiChatDetails.messages;
      if (!messages) return;
      const message = messages.find((m) => m.runId === runId);
      if (message) {
        message.feedback = feedback;
      }
    },
  },
});

export const {
  setActiveAgent,
  resetActiveAgent,
  createGhostChat,
  setTempChatId,
  setActiveChat,
  replaceTempWithChatIdAndName,
  deleteTempChat,
  addAsyncResponse,
  addSyncMessageToActiveChat,
  addSyncMessageToChat,
  patchLastUserMessage,
  setLoadingChatsList,
  setChatsList,
  setLoadingChatDetails,
  clearLoadingChatDetails,
  setChat,
  replaceChatMessages,
  setLoadingChatListItem,
  setChatName,
  deleteChat,
  deleteAgentChats,
  setChatIsFavorite,
  setLoadingSearch,
  setSearchResult,
  clearSearch,
  resetAnimateName,
  resetActiveChatAndSearch,
  addAsyncResponseChunk,
  finishAsyncResponse,
  setResearchInProgress,
  markArtifactContentProcessed,
  setMessageAnimationProgress,
  stopResponse,
  setMessageFeedback,
  setBuildDefinitionStatus,
  setBuildDefinitionData,
  setFoldersLoading,
  setFolders,
  addFolder,
  renameFolder,
  deleteFolder,
  setChatFolderId,
  setPendingChatFolderId,
} = agentsChatsSlice.actions;

export default agentsChatsSlice.reducer;
